import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import { onboardingAPI } from '../../services/api';

// 9 intenciones. La acción de cierre (landing) depende de qué intent quede
// elegido como "primero". join_company_* pasa el rol pre-marcado por query.
const INTENTS = [
  { key: 'find_supplier',          group: 'me',      icon: 'Search',     route: '/advanced-search-results' },
  { key: 'request_quote',          group: 'me',      icon: 'FileText',   route: '/dashboard/quotes?view=enviadas' },
  { key: 'find_job',               group: 'me',      icon: 'Briefcase',  route: '/jobs' },
  { key: 'explore',                group: 'me',      icon: 'Eye',        route: '/dashboard', exclusive: true },
  { key: 'register_company',       group: 'company', icon: 'Building2',  route: '/business-registration?mode=full' },
  { key: 'join_company_admin',     group: 'company', icon: 'Crown',      route: '/dashboard/associations?role=admin_empresa' },
  { key: 'join_company_sales',     group: 'company', icon: 'BarChart3',  route: '/dashboard/associations?role=ventas' },
  { key: 'join_company_marketing', group: 'company', icon: 'Megaphone',  route: '/dashboard/associations?role=marketing' },
  { key: 'join_company_hr',        group: 'company', icon: 'Users',      route: '/dashboard/associations?role=rrhh' },
];

// label key suffix → match con i18n `onboardingPost.intent{Suffix}` / `Desc`.
const INTENT_LABEL_SUFFIX = {
  find_supplier:          'FindSupplier',
  request_quote:          'RequestQuote',
  find_job:               'FindJob',
  explore:                'Explore',
  register_company:       'Register',
  join_company_admin:     'JoinAdmin',
  join_company_sales:     'JoinSales',
  join_company_marketing: 'JoinMarketing',
  join_company_hr:        'JoinHr',
};

// tour cards por intent — 2-3 atajos por sección que pintamos en el paso 4.
const TOUR_CARDS = {
  find_supplier:          [{ icon: 'Search',        route: '/advanced-search-results',                   labelKey: 'tour_findSupplier_1' },
                           { icon: 'Filter',        route: '/advanced-search-results',                   labelKey: 'tour_findSupplier_2' },
                           { icon: 'Heart',         route: '/dashboard/favorites',                       labelKey: 'tour_findSupplier_3' }],
  request_quote:          [{ icon: 'Plus',          route: '/comunidad?crear=1',                         labelKey: 'tour_requestQuote_1' },
                           { icon: 'Sparkles',      route: '/comunidad',                                 labelKey: 'tour_requestQuote_2' },
                           { icon: 'Send',          route: '/dashboard/quotes?view=enviadas',            labelKey: 'tour_requestQuote_3' }],
  find_job:               [{ icon: 'Briefcase',     route: '/jobs',                                      labelKey: 'tour_findJob_1' },
                           { icon: 'IdCard',        route: '/dashboard/edit-profile',                    labelKey: 'tour_findJob_2' },
                           { icon: 'ClipboardList', route: '/jobs',                                      labelKey: 'tour_findJob_3' }],
  explore:                [{ icon: 'Eye',           route: '/dashboard',                                 labelKey: 'tour_explore_1' },
                           { icon: 'BookOpen',      route: '/industry-blog',                             labelKey: 'tour_explore_2' }],
  register_company:       [{ icon: 'Building2',     route: '/business-registration?mode=full',           labelKey: 'tour_register_1' },
                           { icon: 'Users',         route: '/dashboard/associations',                    labelKey: 'tour_register_2' },
                           { icon: 'Inbox',         route: '/dashboard/quotes?view=recibidas',           labelKey: 'tour_register_3' }],
  join_company_admin:     [{ icon: 'Search',        route: '/dashboard/associations?role=admin_empresa', labelKey: 'tour_joinAdmin_1' },
                           { icon: 'Building2',     route: '/dashboard/business',                        labelKey: 'tour_joinAdmin_2' },
                           { icon: 'CreditCard',    route: '/dashboard/subscription',                    labelKey: 'tour_joinAdmin_3' }],
  join_company_sales:     [{ icon: 'Search',        route: '/dashboard/associations?role=ventas',        labelKey: 'tour_joinSales_1' },
                           { icon: 'Inbox',         route: '/dashboard/quotes?view=recibidas',           labelKey: 'tour_joinSales_2' },
                           { icon: 'Package',       route: '/dashboard/products',                        labelKey: 'tour_joinSales_3' }],
  join_company_marketing: [{ icon: 'Search',        route: '/dashboard/associations?role=marketing',     labelKey: 'tour_joinMarketing_1' },
                           { icon: 'Star',          route: '/dashboard/business',                        labelKey: 'tour_joinMarketing_2' },
                           { icon: 'BarChart3',     route: '/dashboard/analytics',                       labelKey: 'tour_joinMarketing_3' }],
  join_company_hr:        [{ icon: 'Search',        route: '/dashboard/associations?role=rrhh',          labelKey: 'tour_joinHr_1' },
                           { icon: 'Plus',          route: '/dashboard/jobs?new=1',                      labelKey: 'tour_joinHr_2' },
                           { icon: 'ClipboardList', route: '/dashboard/jobs',                            labelKey: 'tour_joinHr_3' }],
};

const TOTAL_STEPS = 3;

const OnboardingModal = ({ user, onComplete, initialIntent }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Si viene con initialIntent válido desde URL (ej. CTA comercial del
  // landing), preseleccionamos ese intent y saltamos al step del tour —
  // el user ya expresó su intención, no la volvemos a preguntar.
  const validInitialIntent = useMemo(
    () => (initialIntent && INTENTS.some(i => i.key === initialIntent)) ? initialIntent : null,
    [initialIntent]
  );
  const [step, setStep] = useState(validInitialIntent ? 2 : 0); // 0 welcome, 1 intent, 2 next
  // Merge (no sobrescribir): si el user ya tenía intents previos, agregamos
  // el nuevo sin perder los anteriores. Deduplicado con Set.
  const existingIntents = Array.isArray(user?.onboarding_intents) ? user.onboarding_intents : [];
  const initial = validInitialIntent
    ? Array.from(new Set([validInitialIntent, ...existingIntents]))
    : existingIntents;
  const [selected, setSelected] = useState(initial);
  const [saving, setSaving] = useState(false);

  // Si saltamos al tour por initialIntent, persistimos silencioso el intent
  // mergeado (sin tocar `completed` — lo maneja finalize/skip cuando el user
  // termine el flujo). Idempotente: la API es PATCH.
  useEffect(() => {
    if (!validInitialIntent) return;
    onboardingAPI.update({ intents: initial })
      .catch(e => console.warn('onboarding auto-intent persist failed', e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Esc cierra (= skip). Bloquea scroll del body.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') skip(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persist = useCallback(async (patch) => {
    try {
      setSaving(true);
      await onboardingAPI.update(patch);
    } catch (e) { console.warn('onboarding persist failed', e); }
    finally { setSaving(false); }
  }, []);

  const toggleIntent = (key) => {
    const exclusive = INTENTS.find(i => i.key === key)?.exclusive;
    setSelected(prev => {
      if (exclusive) return prev.includes(key) ? [] : [key];
      if (prev.includes(key)) return prev.filter(x => x !== key);
      return [...prev.filter(x => !INTENTS.find(i => i.key === x)?.exclusive), key];
    });
  };

  const finalize = async (intent) => {
    await persist({ intents: selected, completed: true });
    onComplete?.();
    const target = INTENTS.find(i => i.key === intent)?.route || '/dashboard';
    navigate(target);
  };

  const skip = async () => {
    await persist({ completed: true });
    onComplete?.();
  };

  const goNext = async () => {
    if (step === 1) await persist({ intents: selected });
    setStep(s => Math.min(s + 1, TOTAL_STEPS - 1));
  };
  const goBack = () => setStep(s => Math.max(s - 1, 0));

  const canAdvanceIntent = selected.length > 0;
  const meIntents      = useMemo(() => INTENTS.filter(i => i.group === 'me'),      []);
  const companyIntents = useMemo(() => INTENTS.filter(i => i.group === 'company'), []);

  const renderIntentCard = (intent) => {
    const sfx = INTENT_LABEL_SUFFIX[intent.key];
    const isOn = selected.includes(intent.key);
    return (
      <button
        key={intent.key}
        type="button"
        onClick={() => toggleIntent(intent.key)}
        className={`relative text-left p-4 rounded-xl border-2 transition-all ${
          isOn ? 'border-primary bg-primary/5 shadow-sm' : 'border-border hover:border-primary/40 bg-card'
        }`}
        aria-pressed={isOn}
      >
        {isOn && (
          <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-primary text-primary-foreground inline-flex items-center justify-center shadow">
            <Icon name="Check" size={14} strokeWidth={3} />
          </div>
        )}
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
          <Icon name={intent.icon} size={20} className="text-primary" />
        </div>
        <p className="font-bold text-sm text-foreground">
          {t(`onboardingPost.intent${sfx}`)}
        </p>
        <p className="text-xs text-muted-foreground mt-1 leading-snug">
          {t(`onboardingPost.intent${sfx}Desc`)}
        </p>
      </button>
    );
  };

  const renderTour = () => {
    const chosen = selected.length > 0 ? selected : ['explore'];
    return (
      <div className="space-y-6">
        {chosen.length > 1 && (
          <div className="p-4 rounded-xl bg-primary/[0.04] border border-primary/15">
            <p className="text-sm font-semibold text-foreground mb-3">
              {t('onboardingPost.nextCtaPick')}
            </p>
            <div className="flex flex-wrap gap-2">
              {chosen.map(key => {
                const sfx = INTENT_LABEL_SUFFIX[key];
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => finalize(key)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-card border border-border hover:border-primary text-sm font-semibold text-foreground rounded-lg transition-colors"
                  >
                    <Icon name={INTENTS.find(i => i.key === key)?.icon || 'ArrowRight'} size={14} />
                    {t(`onboardingPost.intent${sfx}`)}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="space-y-5">
          {chosen.map(intentKey => {
            const sfx = INTENT_LABEL_SUFFIX[intentKey];
            const cards = TOUR_CARDS[intentKey] || [];
            return (
              <div key={intentKey}>
                <h4 className="text-xs font-bold tracking-wide uppercase text-muted-foreground mb-2">
                  {t(`onboardingPost.intent${sfx}`)}
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {cards.map((c, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={async () => { await persist({ intents: selected, completed: true }); onComplete?.(); navigate(c.route); }}
                      className="text-left p-3 bg-card border border-border hover:border-primary/50 rounded-xl transition-colors group"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <Icon name={c.icon} size={16} className="text-primary" />
                        </div>
                        <span className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                          {t(`onboardingPost.${c.labelKey}`)}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {chosen.length === 1 && (
          <div className="text-center pt-2">
            <Button
              variant="default"
              size="lg"
              iconName="ArrowRight"
              iconPosition="right"
              onClick={() => finalize(chosen[0])}
              loading={saving}
            >
              {t('onboardingPost.finish')}
            </Button>
          </div>
        )}
      </div>
    );
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[1200] flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-card rounded-2xl shadow-elevated w-full max-w-3xl my-8 max-h-[92vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="text-xs font-semibold text-muted-foreground tracking-wide">
            {step > 0 && t('onboardingPost.stepOf', { current: step, total: TOTAL_STEPS - 1 })}
          </div>
          <button
            type="button"
            onClick={skip}
            className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            {t('onboardingPost.skipForNow')}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 sm:p-8">
          {step === 0 && (
            <div className="text-center max-w-xl mx-auto py-6">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 inline-flex items-center justify-center mb-4">
                <Icon name="Sparkles" size={28} className="text-primary" />
              </div>
              <h2 className="text-2xl font-extrabold tracking-tight text-foreground mb-2">
                {t('onboardingPost.welcomeTitle')}{user?.first_name ? `, ${user.first_name}` : ''}
              </h2>
              <p className="text-base text-muted-foreground">
                {t('onboardingPost.welcomeSubtitle')}
              </p>
              <Button
                variant="default"
                size="lg"
                iconName="ArrowRight"
                iconPosition="right"
                className="mt-6"
                onClick={() => setStep(1)}
              >
                {t('onboardingPost.welcomeCta')}
              </Button>
            </div>
          )}

          {step === 1 && (
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-semibold text-foreground mb-1">{t('onboardingPost.intentTitle')}</h2>
                <p className="text-sm text-muted-foreground">{t('onboardingPost.intentSubtitle')}</p>
                <p className="text-xs text-muted-foreground mt-1">{t('onboardingPost.intentExclusiveHint')}</p>
              </div>

              <h3 className="text-xs font-bold tracking-wide uppercase text-muted-foreground mb-2">
                {t('onboardingPost.intentForMe')}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                {meIntents.map(renderIntentCard)}
              </div>

              <h3 className="text-xs font-bold tracking-wide uppercase text-muted-foreground mb-2">
                {t('onboardingPost.intentForCompany')}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
                {companyIntents.map(renderIntentCard)}
              </div>

              {!canAdvanceIntent && (
                <p className="text-xs text-muted-foreground mt-4">{t('onboardingPost.intentMinHint')}</p>
              )}
            </div>
          )}

          {step === 2 && (
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-semibold text-foreground mb-1">{t('onboardingPost.nextTitle')}</h2>
                <p className="text-sm text-muted-foreground">{t('onboardingPost.nextSubtitle')}</p>
              </div>
              {renderTour()}
            </div>
          )}
        </div>

        {step > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-border bg-muted/30">
            <Button variant="ghost" size="sm" iconName="ChevronLeft" iconPosition="left" onClick={goBack}>
              {t('onboardingPost.back')}
            </Button>
            {step < TOTAL_STEPS - 1 && (
              <Button
                variant="default"
                size="sm"
                iconName="ChevronRight"
                iconPosition="right"
                onClick={goNext}
                disabled={step === 1 && !canAdvanceIntent}
                loading={saving}
              >
                {t('onboardingPost.next')}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default OnboardingModal;
